/**
 * CockpitChat — the right-side chat of the Traction (Monetize) cockpit.
 *
 * ONE Aria across surfaces. This chat now goes through the unified
 * `POST /api/aria/chat` endpoint, so the agent has the SAME full context
 * (Code Truth · Revenue Plan · Loop · Personas · Memory · Drifts) as the
 * Build · Workshop · Execution chats.
 *
 * What it does:
 *   - Owns local conversation state (no HQ polling, no duplicate-messages).
 *   - Renders the AriaThinkingTrail above each agent reply so the founder
 *     SEES which contexts were loaded (Code ✓ · Plan ✓ · Drifts ✓).
 *   - Surfaces the parent's `ariaLog` (real-time narration of the README →
 *     Dataroom → Revenue Plan pipeline) as inline progress chips so the
 *     chat feels alive while the heavy generators run in the background.
 *   - Pre-fills the first user message from `initialPrompt` (Traction
 *     bubble entry-point) and auto-sends it.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Loader2, User, Sparkles, Compass, Target, Users, TrendingUp } from 'lucide-react';
import AriaThinkingTrail from '../aria/AriaThinkingTrail';
import ProposalCards from '../aria/ProposalCards';
import { fetchAriaChat } from '../../lib/ariaChat';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ZONE_HINTS = [
  { icon: Compass, label: 'Scan competitors', prompt: 'Scan the market and surface my 3 strongest competitors with positioning.' },
  { icon: Users, label: 'Map ICP', prompt: 'Map my ICP — 3 sharp personas with pain points and channels.' },
  { icon: Target, label: 'Traction paths', prompt: 'Map my paths to the first dollar, ranked by speed and leverage.' },
  { icon: TrendingUp, label: 'Find influencers', prompt: 'Identify 5 LinkedIn / TikTok / Insta creators I can partner with.' },
];

export default function CockpitChat({ venture, palette, initialPrompt, ariaLog }) {
  const monetizeId = venture?.monetize_id;
  const [turns, setTurns] = useState([]);          // [{role:'user'|'agent'|'progress', ...}]
  const [text, setText] = useState('');
  const [thinking, setThinking] = useState(false);
  const [lastTrail, setLastTrail] = useState(null);
  const scrollerRef = useRef(null);
  const sentInitial = useRef(false);
  const seenLogIds = useRef(new Set());

  // Merge progress narration emitted by the parent during generation into
  // the conversation as `progress` rows (dedup by message_id).
  useEffect(() => {
    if (!Array.isArray(ariaLog) || ariaLog.length === 0) return;
    const incoming = ariaLog.filter(e => e?.message_id && !seenLogIds.current.has(e.message_id));
    if (incoming.length === 0) return;
    incoming.forEach(e => seenLogIds.current.add(e.message_id));
    setTurns(prev => [
      ...prev,
      ...incoming.map(e => ({
        role: 'progress',
        kind: e.kind || 'progress',
        content: e.message || '',
        at: e.created_at,
        id: e.message_id,
      })),
    ]);
  }, [ariaLog]);

  // Auto-send the entry-point prompt once.
  useEffect(() => {
    if (!initialPrompt || sentInitial.current || !monetizeId) return;
    sentInitial.current = true;
    const t = setTimeout(() => send(initialPrompt), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, monetizeId]);

  // Auto-scroll on every change.
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [turns, thinking, lastTrail]);

  const send = async (msgText) => {
    const value = (msgText ?? text).trim();
    if (!value || !monetizeId || thinking) return;
    const now = new Date().toISOString();
    const history = turns
      .filter(t => t.role === 'user' || t.role === 'agent')
      .slice(-12)
      .map(t => ({ role: t.role === 'agent' ? 'agent' : 'user', content: t.content }));

    setText('');
    setTurns(prev => [...prev, { role: 'user', content: value, at: now, id: `u_${Date.now()}` }]);
    setThinking(true);
    setLastTrail(null);

    try {
      const data = await fetchAriaChat(API, {
        project_id: monetizeId,
        persona: 'aria',
        surface: 'monetize',
        message: value,
        history,
      });
      if (data.ok) {
        setLastTrail(data.trail || null);
        setTurns(prev => [...prev, {
          role: 'agent',
          content: data.reply || '',
          at: data.at || new Date().toISOString(),
          trail: data.trail || null,
          proposals: data.applied_changes || data.proposals || [],
          drifts: data.drifts_detected || [],
          id: `a_${Date.now()}`,
        }]);
      } else {
        setTurns(prev => [...prev, {
          role: 'system',
          content: `Aria could not respond (${data.error || 'unknown error'}). Try again.`,
          at: new Date().toISOString(),
          id: `s_${Date.now()}`,
        }]);
      }
    } catch (err) {
      setTurns(prev => [...prev, {
        role: 'system',
        content: 'Network error · check your connection.',
        at: new Date().toISOString(),
        id: `s_${Date.now()}`,
      }]);
    } finally {
      setThinking(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const visibleTurns = useMemo(
    () => turns.sort((a, b) => new Date(a.at) - new Date(b.at)),
    [turns],
  );
  const hasUserSentSomething = turns.some(t => t.role === 'user');

  return (
    <aside
      data-testid="cockpit-chat"
      style={{
        width: 380, flexShrink: 0, height: '100%',
        background: palette.surface,
        borderLeft: `1px solid ${palette.border}`,
        display: 'flex', flexDirection: 'column',
        color: palette.text,
      }}
    >
      {/* Header */}
      <div style={{
        padding: '14px 18px',
        borderBottom: `1px solid ${palette.border}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{
          width: 32, height: 32,
          background: `linear-gradient(135deg, ${palette.accent}, ${palette.accent2 || palette.accent})`,
          color: '#FFFFFF',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 12, letterSpacing: '0.04em',
        }}>
          AR
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: palette.text, letterSpacing: '-0.01em' }}>
            Aria · The Architect
          </div>
          <div style={{
            fontSize: 9.5, color: palette.textDim, letterSpacing: '0.06em',
            display: 'flex', alignItems: 'center', gap: 4, marginTop: 2,
          }}>
            <span style={{
              width: 5, height: 5, borderRadius: 999,
              background: palette.positive, boxShadow: `0 0 6px ${palette.positive}`,
            }} />
            UNIFIED CONTEXT · CODE-AWARE
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div
        ref={scrollerRef}
        style={{
          flex: 1, overflowY: 'auto', padding: '16px 16px 8px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}
      >
        {visibleTurns.length === 0 && !thinking && (
          <div style={{
            margin: 'auto', textAlign: 'center', padding: '24px 18px',
            color: palette.textDim, fontSize: 12, lineHeight: 1.55,
            fontStyle: 'italic',
          }}>
            Aria has the full venture in context — code · plan · personas ·
            memory. Ask anything about positioning, ICP, pricing or the next
            move to the first dollar.
          </div>
        )}
        {visibleTurns.map(t => (
          <Bubble key={t.id} turn={t} palette={palette} />
        ))}
        {thinking && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lastTrail && lastTrail.length > 0 && (
              <AriaThinkingTrail trail={lastTrail} pulsing compact />
            )}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', fontSize: 11, color: palette.textMute,
            }}>
              <Loader2 size={11} style={{ animation: 'spin 0.7s linear infinite' }} />
              Aria is reading the venture…
            </div>
          </div>
        )}
      </div>

      {/* Suggestion chips — only before the founder writes */}
      {!hasUserSentSomething && !thinking && (
        <div style={{
          padding: '6px 14px 12px',
          borderTop: `1px solid ${palette.border}`,
          display: 'flex', gap: 6, flexWrap: 'wrap',
        }}>
          {ZONE_HINTS.map((h, i) => (
            <button
              key={i}
              onClick={() => send(h.prompt)}
              data-testid={`hint-chip-${i}`}
              style={{
                fontSize: 10.5, padding: '5px 10px',
                background: palette.surface2, color: palette.textMute,
                border: `1px solid ${palette.border}`,
                borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 5,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = palette.accent + '99'; e.currentTarget.style.color = palette.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = palette.border; e.currentTarget.style.color = palette.textMute; }}
            >
              <h.icon size={11} />{h.label}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <div style={{
        padding: 12, borderTop: `1px solid ${palette.border}`,
        background: palette.surface2,
      }}>
        <div style={{
          background: palette.bg, border: `1px solid ${palette.border}`,
          borderRadius: 12, padding: '8px 10px',
          display: 'flex', gap: 8, alignItems: 'flex-end',
        }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={onKey}
            placeholder="Tell Aria what to surface…"
            data-testid="cockpit-chat-input"
            disabled={thinking}
            rows={1}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none', outline: 'none', resize: 'none',
              color: palette.text,
              fontFamily: 'inherit',
              fontSize: 13, lineHeight: 1.5,
              maxHeight: 130,
              caretColor: palette.accent,
            }}
          />
          <button
            onClick={() => send()}
            disabled={!text.trim() || thinking}
            data-testid="cockpit-chat-send"
            style={{
              width: 32, height: 32, flexShrink: 0,
              background: !text.trim() || thinking ? palette.surface : palette.accent,
              color: !text.trim() || thinking ? palette.textDim : '#0F0F1A',
              border: 'none', borderRadius: 8,
              cursor: thinking ? 'wait' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
          >
            {thinking
              ? <Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} />
              : <Send size={13} />}
          </button>
        </div>
        <div style={{
          fontSize: 10, color: palette.textDim,
          marginTop: 6, letterSpacing: '0.02em',
        }}>
          Aria reads the actual code · drifts and canonical decisions surface here.
        </div>
      </div>
    </aside>
  );
}

/* ────────────────── Bubble ────────────────── */

function Bubble({ turn, palette }) {
  if (turn.role === 'progress') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{
          width: 22, height: 22, flexShrink: 0,
          background: `${palette.accent}22`, color: palette.accent,
          border: `1px solid ${palette.accent}55`, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Sparkles size={10} />
        </div>
        <div style={{
          maxWidth: '85%',
          padding: '7px 10px',
          background: palette.surface2,
          border: `1px solid ${palette.accent}30`,
          borderRadius: 10,
          fontSize: 11.5, lineHeight: 1.5,
          color: palette.textMute,
          fontStyle: 'italic',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            fontSize: 9, fontWeight: 800, letterSpacing: '0.16em',
            color: palette.accent, marginBottom: 4, textTransform: 'uppercase',
          }}>
            {turn.kind === 'thinking' ? 'Thinking' : 'Progress'}
          </div>
          {turn.content}
        </div>
      </div>
    );
  }

  if (turn.role === 'system') {
    return (
      <div style={{
        padding: '8px 11px',
        background: '#FEF2F2', color: '#B91C1C',
        border: '1px solid #FECACA', borderRadius: 10,
        fontSize: 11.5,
      }}>
        {turn.content}
      </div>
    );
  }

  const isUser = turn.role === 'user';
  const accent = isUser ? (palette.accent2 || palette.accent) : palette.accent;

  return (
    <div style={{
      display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row',
      gap: 8, alignItems: 'flex-start',
    }}>
      <div style={{
        width: 26, height: 26, flexShrink: 0,
        background: `${accent}22`, color: accent,
        border: `1px solid ${accent}55`, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 800,
      }}>
        {isUser ? <User size={12} /> : 'A'}
      </div>
      <div style={{ maxWidth: '85%', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {!isUser && turn.trail && (
          <AriaThinkingTrail trail={turn.trail} pulsing={false} compact />
        )}
        <div style={{
          padding: '9px 12px',
          background: isUser ? palette.surface2 : palette.surface,
          border: `1px solid ${palette.border}`,
          borderRadius: 12,
          borderTopRightRadius: isUser ? 4 : 12,
          borderTopLeftRadius: !isUser ? 4 : 12,
          fontSize: 12, lineHeight: 1.55,
          color: palette.text,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {!isUser && (
            <div style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '0.16em',
              color: accent, marginBottom: 4, textTransform: 'uppercase',
            }}>
              Aria
            </div>
          )}
          {turn.content}
        </div>
        {!isUser && Array.isArray(turn.proposals) && turn.proposals.length > 0 && (
          <ProposalCards proposals={turn.proposals} accent={palette.accent} />
        )}
        {!isUser && Array.isArray(turn.drifts) && turn.drifts.length > 0 && (
          <div style={{
            fontSize: 10, color: '#B91C1C',
            background: '#FEF2F2', border: '1px solid #FECACA',
            padding: '5px 8px', borderRadius: 8,
          }}>
            ⚠ {turn.drifts.length} drift(s) detected · {turn.drifts.map(d => d.topic).join(' · ')}
          </div>
        )}
      </div>
    </div>
  );
}
